import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, getTotal, clearCart } = useCart();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Redirect if cart is empty
  if (cartItems.length === 0) {
    return (
      <div className="bg-navy text-gray-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">No items to checkout</h1>
          <button
            onClick={() => navigate('/cart')}
            className="bg-primary hover:bg-primary-dark text-white font-medium py-3 px-8 rounded-md transition"
          >
            Back to Cart
          </button>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zipCode'];
    for (let field of required) {
      if (!formData[field]?.trim()) {
        setError(`${field.replace(/([A-Z])/g, ' $1').trim()} is required`);
        return false;
      }
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare cart items data
      const cartItemsData = cartItems.map(item => ({
        productName: item.name,
        quantity: item.quantity,
        size: item.selectedSize,
        color: item.selectedColor,
        price: item.price,
        subtotal: item.price * item.quantity
      }));

      const orderData = {
        customerInfo: formData,
        cartItems: cartItemsData,
        orderTotal: getCartTotal(),
        orderDate: new Date().toISOString()
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit order');
      }

      const data = await response.json();

      setSuccess(true);
      clearCart();

      // Redirect to success page after 2 seconds
      setTimeout(() => {
        navigate('/order-success', { state: { orderId: data.orderId } });
      }, 2000);

    } catch (err) {
      console.error('Error submitting order:', err);
      setError(err.message || 'Failed to submit order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-navy text-gray-800 min-h-screen py-10">
      <div className="container mx-auto px-4">
        {/* Header */}
        <button
          onClick={() => navigate('/cart')}
          className="flex items-center gap-2 text-primary hover:text-primary-light mb-8 transition"
        >
          <ChevronLeft size={20} />
          Back to Cart
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <div className="bg-navy-light border border-primary/20 rounded-lg p-8">
              <h1 className="text-3xl font-bold mb-8">Checkout</h1>

              {error && (
                <div className="bg-red-500/10 border border-red-500 text-red-600 p-4 rounded-md mb-6">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-500/10 border border-green-500 text-green-600 p-4 rounded-md mb-6">
                  Order submitted successfully! Redirecting...
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-gray-900">Personal Information</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="John"
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Doe"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-gray-900">Contact Information</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="john@example.com"
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="+1 (555) 123-4567"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-gray-900">Shipping Address</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Address *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="123 Main Street"
                        disabled={loading}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">City *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="New York"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">State/Province *</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="NY"
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">ZIP Code *</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="10001"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Country</label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 bg-navy border border-primary/30 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="USA"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Place Order'}
                </button>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-navy-light border border-primary/20 rounded-lg p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6 pb-6 border-b border-primary/20 max-h-96 overflow-y-auto">
                {cartItems.map(item => (
                  <div key={item.cartItemId} className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">{item.name}</span>
                      <span className="text-gray-600 text-sm">×{item.quantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 text-xs">Size: {item.selectedSize}</span>
                      <span className="text-primary font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-600">${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-green-600">${getCartTotal().toFixed(2) > 50 ? 'Free' : '5'}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-3 border-t border-primary/20">
                  <span>Total</span>
                  <span className="text-primary">${getTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;